import {isSafe} from '@shopify/safe-redirect';
import {getSerialized} from '@shopify/react-html';

import {ImageSize} from '../images';

const DUMMY_DOMAIN = 'https://shopify.com';

export function isAbsolute(url: string) {
  return url.startsWith('http');
}

export function stripHost(url: string) {
  const urlObject = new URL(url, DUMMY_DOMAIN);
  return `${urlObject.pathname}${urlObject.search}${urlObject.hash}`;
}

export function pathnameFor(url: string) {
  try {
    return new URL(url, DUMMY_DOMAIN).pathname;
  } catch {
    // Invalid URL. Not a great choice for pathname, but nothing is
    // really any better.
    return '/';
  }
}

export function pathnameAndSearchFor(url: string) {
  try {
    const urlObject = new URL(url, DUMMY_DOMAIN);
    return `${urlObject.pathname}${urlObject.search}`;
  } catch {
    // Invalid URL. Not a great choice for pathname, but nothing is
    // really any better.
    return '/';
  }
}

export function getResizedImage(url: string, imageSize: string) {
  const imageSizeRegex = /_[a-z0-9]+(?=\.[a-z]{3,4}\?)/i;
  const newImageUrl = imageSize
    ? url.replace(imageSizeRegex, `_${imageSize}`)
    : url.replace(imageSizeRegex, '');
  return newImageUrl;
}

export function getSizedImage(url: string, imageSize: string) {
  if (!imageSize) {
    return url;
  }
  const imageExtensionRegex = /\.[a-z0-9]{3,4}\?/i;
  const imageUrlExt = url.match(imageExtensionRegex) || '';
  return url.replace(imageExtensionRegex, `_${imageSize}${imageUrlExt[0]}`);
}

export function getImageSizeFromUrl(url: string) {
  const imageSizeRegex = /_[a-z0-9]+(?=\.[a-z]{3,4}\?)/i;
  const matchArray = url.match(imageSizeRegex) as string[];
  let currentImageSize = ImageSize.Original;
  if (matchArray) {
    const size = matchArray[0].substr(1);
    currentImageSize = isValidImageSize(size)
      ? (size as ImageSize)
      : ImageSize.Original;
  }
  return currentImageSize;
}

export function isValidImageSize(size: string) {
  return Object.values(ImageSize).includes(size as ImageSize);
}

export function isHttpSecure(url: string) {
  const httpUrlRegex = /^https:\/\//i;
  return httpUrlRegex.test(url);
}

export function isValid(url: string) {
  const validUrlRegex = /^http(s)?:\/\//i;
  return validUrlRegex.test(url);
}

const CUSTOM_PROTOCOL_REGEX = /^(?!http)\w+:/;

export function isNonHttpProtocol(url: string) {
  return CUSTOM_PROTOCOL_REGEX.test(url);
}

export function getNormalizedURL(path: string, ignoreInvalidProtocols = true) {
  try {
    const initialURL = new URL(path);

    if (
      ignoreInvalidProtocols &&
      initialURL.protocol !== 'http:' &&
      initialURL.protocol !== 'https:'
    ) {
      return initialURL;
    }
  } catch {
    // Ignoring invalid URLs
  }

  // convert: 'some/path' to '/some/path'
  let normalizedPath = path;
  if (path.indexOf('//') === -1 && path[0] !== '/') {
    normalizedPath = `/${path}`;
  }

  // convert: '/some/path' to '<current-origin>/some/path'
  const anchor = document.createElement('a');
  anchor.href = normalizedPath;

  return new URL(anchor.href);
}

export function getURLSegments(url: URL) {
  const path = url.pathname;
  return path.startsWith('/') ? path.substring(1).split('/') : path.split('/');
}

export function getAuthority(url: URL) {
  const password = url.password ? `:${url.password}` : '';
  return [url.username ? `${url.username}${password}@` : '', url.host].join('');
}

export function getOrigin(url: string) {
  const uri = getNormalizedURL(url);
  return `${uri.protocol}//${getAuthority(uri)}`;
}

export function getYoutubeVideoId(url: string) {
  const match = url.match(
    /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/,
  );

  if (match && match[1]) {
    return match[1];
  }
}

export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}

export function getVimeoVideoId(url: string) {
  // Support for the various forms a Vimeo video can come in. Currently do not support ondemand videos.
  const regularVideo = /.*(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?<id>\d+)/;
  const channelVideo =
    /.*(?:https?:\/\/)?(?:www\.)?vimeo\.com\/channels\/\w+\/(?<id>\d+)/;
  const groupVideo =
    /.*(?:https?:\/\/)?(?:www\.)?vimeo\.com\/groups\/\w+\/videos\/(?<id>\d+)/;
  const playerVideo = /.*(?:https?:\/\/)?player\.vimeo\.com\/video\/(?<id>\d+)/;

  for (const regex of [regularVideo, channelVideo, playerVideo, groupVideo]) {
    const match = url.match(regex);
    if (match && match.groups?.id) {
      return match.groups?.id;
    }
  }
}

export function vimeoEmbedUrl(videoId: string): string {
  return `https://player.vimeo.com/video/${videoId}`;
}

export function isSameOrigin(url: string) {
  const origin = getOrigin(url);
  return origin === location.origin;
}

export function isSubdomain(originalUrl: URL, newUrl: URL) {
  return (
    originalUrl.protocol === newUrl.protocol &&
    originalUrl.port === newUrl.port &&
    newUrl.hostname.slice(-originalUrl.hostname.length, -1) ===
      originalUrl.hostname &&
    originalUrl.hostname !== ''
  );
}

export function resolveAdminPath(href = '', adminPath = '/admin') {
  const url = getNormalizedURL(href, false);
  const segments = getURLSegments(url);
  const pathPrefix = ensureForwardSlash(adminPath);
  // Most browsers automatically prefix the pathname with a forward slash, but not all, so we manually prefix if needed
  url.pathname = ensureForwardSlash(url.pathname);

  if (segments.length === 0 || ensureForwardSlash(segments[0]) !== pathPrefix) {
    url.pathname = `${pathPrefix}${url.pathname}`;
  }

  // History does an additional URI decode, so we test the path after a decode
  // See https://github.com/Shopify/web/issues/27646
  const verifiedAdminPath = isSafe(decodeURIComponent(url.pathname), {
    matchPath: /^\/admin($|\/)/,
  });

  if (verifiedAdminPath) {
    return `${url.pathname}${url.search}${url.hash}`;
  } else {
    return pathPrefix;
  }
}

export function ensureForwardSlash(pathname: string) {
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function isValidAbsolutePath(path: string) {
  return path.match('^https?://');
}

export function localizeUrl(path: string, locale: string) {
  const url = new URL(path);

  const queryString = url.search;
  const searchParams = new URLSearchParams(queryString);

  searchParams.set('locale', locale || 'en');

  url.search = searchParams.toString();

  return url.toString();
}

export function isDevelopmentShop(host: string) {
  return /(\.myshopify\.io|\.spin\.dev|\.scvm\.io)$/.test(host);
}

export function isUnifiedAdmin() {
  try {
    return Boolean(getSerialized('is-unified-app'));
  } catch {
    return false;
  }
}
